import React from 'react';
import { DateUtil } from "@/utils/dateUtil.ts";
import { PrintUtil } from "@/utils/printUtil.ts";
import type { HeaderConfig } from '@/components/Print/UnifiedPrintModal';

interface DischargeSummaryContentProps {
  data: any;
  translations: any;
  language: string;
  clinicInfo: any;
  doctorInfo: any;
  patientInfo: any;
  settings: any;
  currentUser: any;
  headerConfig?: HeaderConfig;
}

export function DischargeSummaryContent({
                                           data,
                                           clinicInfo,
                                           doctorInfo,
                                           patientInfo,
                                           settings,
                                           currentUser,
                                           headerConfig,
                                         }: Readonly<DischargeSummaryContentProps>) {

  const normalizeText = (value: unknown): string =>
      value === null || value === undefined ? '' : String(value).trim();

  const hasText = (value: unknown): boolean => normalizeText(value).length > 0;

  const clinicName    = clinicInfo?.name    || '';
  const clinicAddress = clinicInfo?.address || '';
  const clinicPhone   = clinicInfo?.phone   || '';
  const doctorName    = settings?.profile?.name   || doctorInfo?.name    || data?.admitting_doctor_name || '';
  const doctorSpec    = settings?.profile?.specialty || doctorInfo?.specialization || doctorInfo?.specialty || '';
  const doctorLicense = settings?.profile?.license   || doctorInfo?.registration_no || '';

  const isDraft = data?.status === 'admitted';
  const meds    = (data?.medicines || []).filter((m: any) => hasText(m?.drug_name));
  const ageGenderInfo = patientInfo?.age
      ? patientInfo
      : { age: data?.patient_age, gender: data?.patient_gender };

  const sectionStyle: React.CSSProperties = { marginBottom: '4mm', fontSize: '11px' };
  const headingStyle: React.CSSProperties = {
    fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase',
    borderBottom: '1px solid #999', paddingBottom: '1mm', marginBottom: '2mm', letterSpacing: '0.5px',
  };

  const Section = ({ title, value }: { title: string; value?: unknown }) =>
      hasText(value) ? (
          <div style={sectionStyle}>
            <div style={headingStyle}>{title}</div>
            <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.4' }}>{normalizeText(value)}</div>
          </div>
      ) : null;

  return (
      <div style={{ position: 'relative' }}>

        {/* Draft watermark — shown until the discharge is finalized */}
        {isDraft && (
            <div style={{
              position: 'absolute', top: '40%', left: '50%',
              transform: 'translate(-50%, -50%) rotate(-30deg)',
              fontSize: '64px', fontWeight: 'bold', color: 'rgba(200,0,0,0.12)',
              letterSpacing: '8px', whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 0,
            }}>
              DRAFT
            </div>
        )}

        {/* Clinic header (configurable) */}
        {(!headerConfig || headerConfig.mode === 'auto') && clinicName && (
            <div style={{ textAlign: 'center', marginBottom: '4mm', borderBottom: '2px solid #000', paddingBottom: '3mm' }}>
              {(!headerConfig || headerConfig.showName) && (
                  <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '1mm' }}>{clinicName}</div>
              )}
              {(!headerConfig || headerConfig.showAddress) && clinicAddress && (
                  <div style={{ fontSize: '10px', color: '#444', marginBottom: '1mm' }}>{clinicAddress}</div>
              )}
              {(!headerConfig || headerConfig.showPhone) && clinicPhone && (
                  <div style={{ fontSize: '10px', color: '#444' }}>Ph: {clinicPhone}</div>
              )}
            </div>
        )}
        {headerConfig?.mode === 'none' && (
            <div style={{ height: `${headerConfig.topSpaceMm}mm` }} />
        )}
        {headerConfig?.mode === 'custom' && headerConfig.customText && (
            <div style={{ textAlign: 'center', marginBottom: '4mm', borderBottom: '2px solid #000', paddingBottom: '3mm', whiteSpace: 'pre-wrap' }}>
              {headerConfig.customText}
            </div>
        )}

        <div style={{ textAlign: 'center', fontSize: '14px', fontWeight: 'bold', letterSpacing: '2px', marginBottom: '4mm' }}>
          DISCHARGE SUMMARY
        </div>

        {/* Patient / admission info */}
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2mm 10mm',
          marginBottom: '4mm', fontSize: '11px', borderBottom: '1px solid #000', paddingBottom: '3mm',
        }}>
          <div><span style={{ fontWeight: 'bold' }}>Patient Name:</span> {PrintUtil.toUpperCaseText(patientInfo?.name) || PrintUtil.toUpperCaseText(data?.patient_name)}</div>
          <div><span style={{ fontWeight: 'bold' }}>UHID / MRN:</span> {patientInfo?.mrn || data?.patient_mrn || 'N/A'}</div>
          <div><span style={{ fontWeight: 'bold' }}>Age / Gender:</span> {PrintUtil.generateAgeGenderString(ageGenderInfo)}</div>
          <div><span style={{ fontWeight: 'bold' }}>IPD No:</span> {data?.admission_number || '—'}</div>
          <div><span style={{ fontWeight: 'bold' }}>Ward / Bed:</span> {data?.ward_name ? `${data.ward_name}${data.bed_number ? ` / Bed ${data.bed_number}` : ''}` : '—'}</div>
          <div><span style={{ fontWeight: 'bold' }}>Admission Date:</span> {DateUtil.formatDateForType(data?.admission_date, 'beautiful-date-time')}</div>
          <div>
            <span style={{ fontWeight: 'bold' }}>Treating Doctor:</span> Dr. {doctorName}
            {hasText(data?.consulting_doctor_name) && <> &nbsp;|&nbsp; <span style={{ fontWeight: 'bold' }}>Consultant:</span> Dr. {data.consulting_doctor_name}</>}
          </div>
          <div><span style={{ fontWeight: 'bold' }}>Discharge Date:</span> {hasText(data?.discharge_date) ? DateUtil.formatDateForType(data.discharge_date, 'beautiful-date-time') : 'Pending'}</div>
        </div>

        <Section title="Chief Complaints"        value={data?.complaints} />
        <Section title="Relevant History"        value={data?.patient_history} />
        <Section title="Findings on Examination" value={data?.key_findings_physical} />
        <Section title="Investigation Summary"   value={data?.investigation_summary} />

        {hasText(data?.final_diagnosis) && (
            <div style={sectionStyle}>
              <div style={headingStyle}>Final Diagnosis</div>
              <div style={{ fontWeight: 'bold' }}>{normalizeText(data.final_diagnosis)}</div>
            </div>
        )}

        <Section title="Procedure / Surgery Done" value={data?.procedures_done} />
        <Section title="Operative Findings"       value={data?.operative_findings} />
        <Section title="Treatment Given"          value={data?.treatment_given} />
        <Section title="Hospital Course"          value={data?.hospital_course} />

        {hasText(data?.condition_at_discharge) && (
            <div style={sectionStyle}>
              <div style={headingStyle}>Condition at Discharge</div>
              <div style={{ fontWeight: 'bold', textTransform: 'capitalize' }}>{normalizeText(data.condition_at_discharge)}</div>
            </div>
        )}

        {/* Discharge medication */}
        {meds.length > 0 && (
            <div style={{ marginBottom: '4mm' }}>
              <div style={headingStyle}>Discharge Medication</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
                <thead>
                <tr>
                  {['#', 'Medicine', 'Type', 'Dose', 'When', 'Frequency', 'Duration', 'Notes'].map(h => (
                      <th key={h} style={{ textAlign: 'left', borderBottom: '1px solid #000', padding: '1mm 2mm', fontWeight: 'bold' }}>{h}</th>
                  ))}
                </tr>
                </thead>
                <tbody>
                {meds.map((m: any, i: number) => (
                    <tr key={i}>
                      <td style={{ padding: '1mm 2mm', borderBottom: '1px solid #ddd' }}>{i + 1}</td>
                      <td style={{ padding: '1mm 2mm', borderBottom: '1px solid #ddd', fontWeight: 'bold' }}>{normalizeText(m.drug_name).toUpperCase()}</td>
                      <td style={{ padding: '1mm 2mm', borderBottom: '1px solid #ddd' }}>{m.drug_type || '—'}</td>
                      <td style={{ padding: '1mm 2mm', borderBottom: '1px solid #ddd' }}>{m.dose || '—'}</td>
                      <td style={{ padding: '1mm 2mm', borderBottom: '1px solid #ddd' }}>{m.taken_when || '—'}</td>
                      <td style={{ padding: '1mm 2mm', borderBottom: '1px solid #ddd' }}>{m.frequency || '—'}</td>
                      <td style={{ padding: '1mm 2mm', borderBottom: '1px solid #ddd' }}>{m.duration || '—'}</td>
                      <td style={{ padding: '1mm 2mm', borderBottom: '1px solid #ddd' }}>{m.notes || ''}</td>
                    </tr>
                ))}
                </tbody>
              </table>
            </div>
        )}

        <Section title="Discharge Advice"      value={data?.discharge_advice} />
        <Section title="Diet Advice"           value={data?.diet_advice} />
        <Section title="Activity Restriction"  value={data?.activity_restriction} />
        <Section title="Additional Medication" value={data?.additional_medication} />
        <Section title="Tests Requested"       value={data?.tests_requested} />
        <Section title="Other Instructions"    value={data?.others} />

        {(hasText(data?.follow_up_date) || hasText(data?.follow_up_notes)) && (
            <div style={sectionStyle}>
              <div style={headingStyle}>Follow-Up</div>
              <div>
                {hasText(data?.follow_up_date) && (
                    <span>
                      <span style={{ fontWeight: 'bold' }}>Date:</span> {DateUtil.formatDateForType(data.follow_up_date, 'date-only')}
                      {hasText(data?.follow_up_notes) ? ' — ' : ''}
                    </span>
                )}
                {normalizeText(data?.follow_up_notes)}
              </div>
            </div>
        )}

        {hasText(data?.acknowledgement) && (
            <div style={{ ...sectionStyle, marginTop: '6mm', fontStyle: 'italic' }}>
              {normalizeText(data.acknowledgement)}
            </div>
        )}

        {/* Signatures */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '15mm', paddingTop: '10mm', fontSize: '10px' }}>
          <div style={{ textAlign: 'center', borderTop: '1px solid #000', paddingTop: '2mm', width: '60mm' }}>
            Patient / Attendant Signature
          </div>
          <div style={{ textAlign: 'center', borderTop: '1px solid #000', paddingTop: '2mm', width: '60mm' }}>
            {doctorName && <div style={{ fontWeight: 'bold' }}>Dr. {doctorName}</div>}
            {doctorSpec && <div>{doctorSpec}</div>}
            {doctorLicense && <div>Reg No: {doctorLicense}</div>}
            <div>Treating Doctor</div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8mm', fontSize: '10px' }}>
          <div><span style={{ fontWeight: 'bold' }}>Generated By </span>{currentUser?.name}</div>
          <div style={{ textAlign: 'right' }}><span style={{ fontWeight: 'bold' }}>Generated On </span>{DateUtil.formatDateForType(new Date().toISOString(), 'date-time')}</div>
        </div>
      </div>
  );
}
